/*! Copyright (c) Safe As Milk. All rights reserved. */
/* OHM v12: intent-loaded, rAF-throttled sticky header with bfcache-safe lifecycle handling. */
class HeaderContainer extends HTMLElement {
  #sticky = false;
  #headerStickyObserver = null;
  #raf = 0;
  #lastScrollTop = 0;
  #navbarHeight = 110;
  #header = null;
  #boundScroll;
  #boundResize;
  #boundPageShow;
  #boundPageHide;

  constructor() {
    super();
    this.#boundScroll = this.#onScroll.bind(this);
    this.#boundResize = this.#onResize.bind(this);
    this.#boundPageShow = this.#onPageShow.bind(this);
    this.#boundPageHide = this.#onPageHide.bind(this);
  }

  connectedCallback() {
    this.#sticky = Boolean(this.dataset.sticky);
    if (!this.#sticky) return;

    this.#header = this.querySelector('.js-header');
    this.#setNavbarHeight();

    if ('IntersectionObserver' in window) {
      this.#headerStickyObserver = new IntersectionObserver(([entry]) => {
        document.body.classList.toggle('header-stuck', !entry.isIntersecting);
      }, { threshold: [1] });
      this.#headerStickyObserver.observe(this);
    }

    if (this.#header?.classList.contains('js-header-scroll')) {
      window.addEventListener('scroll', this.#boundScroll, { passive: true });
      window.addEventListener('resize', this.#boundResize, { passive: true });
      window.addEventListener('pageshow', this.#boundPageShow, { passive: true });
      window.addEventListener('pagehide', this.#boundPageHide, { passive: true });
    }
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #setNavbarHeight() {
    const mobile = window.matchMedia?.('(max-width: 767px)').matches;
    if (mobile) {
      // Avoid a synchronous geometry read on the mobile critical path. The
      // configured mobile header height is rendered into a data attribute.
      const configured = Number.parseFloat(this.dataset.mobileHeight || '60');
      this.#navbarHeight = (Number.isFinite(configured) ? configured : 60) + 50;
      return;
    }

    // Desktop can measure after layout; this path is not used by mobile LH.
    const measured = this.#header ? this.#header.offsetHeight : 60;
    this.#navbarHeight = measured + 50;
  }

  #onScroll() {
    if (this.#raf) return;
    this.#raf = window.requestAnimationFrame(() => {
      this.#raf = 0;
      this.#hasScrolled();
    });
  }

  #onResize() {
    if (this.#raf) return;
    this.#raf = window.requestAnimationFrame(() => {
      this.#raf = 0;
      this.#setNavbarHeight();
      this.#hasScrolled();
    });
  }

  #onPageShow() {
    // Re-sync state when restored from bfcache without creating new observers.
    this.#setNavbarHeight();
    this.#lastScrollTop = window.scrollY || 0;
    this.#hasScrolled();
  }

  #onPageHide() {
    if (this.#raf) {
      window.cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    }
  }

  #hasScrolled(delta = 5) {
    const st = window.scrollY || 0;
    if (Math.abs(this.#lastScrollTop - st) <= delta) return;

    if (st > this.#lastScrollTop && st > this.#navbarHeight) {
      document.body.classList.remove('header-down');
      document.body.classList.add('header-up');
      this.dispatchEvent(new CustomEvent('on:header:sticky', { detail: { visible: false } }));
    } else {
      document.body.classList.remove('header-up');
      document.body.classList.add('header-down');
      this.dispatchEvent(new CustomEvent('on:header:sticky', { detail: { visible: true } }));
    }
    this.#lastScrollTop = st;
  }

  #cleanup() {
    if (this.#headerStickyObserver) {
      this.#headerStickyObserver.disconnect();
      this.#headerStickyObserver = null;
    }
    if (this.#raf) {
      window.cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    }
    window.removeEventListener('scroll', this.#boundScroll);
    window.removeEventListener('resize', this.#boundResize);
    window.removeEventListener('pageshow', this.#boundPageShow);
    window.removeEventListener('pagehide', this.#boundPageHide);
  }
}

if (!customElements.get('header-container')) {
  customElements.define('header-container', HeaderContainer);
}
